import { DOCUMENT } from '@angular/common';
import {
  Directive,
  Input,
  ElementRef,
  HostListener,
  OnDestroy,
  AfterViewInit,
  NgZone,
  Optional,
  Inject,
  Output,
  EventEmitter,
} from '@angular/core';
import { fromEvent, merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const TOP_CSS_CLASS = 'fn-dropdown-top';
const BOTTOM_CSS_CLASS = 'fn-dropdown-bottom';
@Directive({
  selector: '[fnAppendTo]',
})
export class FnAppendToDirective implements AfterViewInit, OnDestroy {
  @Input() fnAppendTo: string;
  private select: HTMLElement;
  private parentElem: HTMLElement;
  private dropDown: HTMLElement;
  private currentPosition: string;
  @Input() isTopPositionOnly = false;
  @Input() isSetWidth = true;
  private readonly destroy$ = new Subject<void>();
  @Output() outsideClick = new EventEmitter<void>();

  constructor(
    private elemRef: ElementRef,
    private ngZone: NgZone,
    @Optional() @Inject(DOCUMENT) private _document: any
  ) { }

  ngAfterViewInit() {
    this.setElement();
  }
  setElement() {
    this.dropDown = this.elemRef.nativeElement as HTMLElement;
    this.select = this.dropDown.parentElement;
    if (this.fnAppendTo) {
      this.parentElem = document.querySelector(this.fnAppendTo) as HTMLElement;
      this.appendDropDown();
    } else {
      this.parentElem = this.dropDown.parentElement;
    }
    this.handleDropdownPosition();
    this.handleOutsideClick();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }

  appendDropDown() {
    this.parentElem.appendChild(this.dropDown);
  }
  private handleOutsideClick() {
    if (!this._document) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      merge(
        fromEvent(this._document, 'touchstart', { capture: true }),
        fromEvent(this._document, 'mousedown', { capture: true })
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe(($event) => this._checkToClose($event));
    });
  }

  private _checkToClose($event: any) {
    if (
      this.select.contains($event.target) ||
      this.dropDown.contains($event.target)
    ) {
      return;
    }
    const path = $event.path || ($event.composedPath && $event.composedPath());
    if (
      $event.target &&
      $event.target.shadowRoot &&
      path &&
      path[0] &&
      this.select.contains(path[0])
    ) {
      return;
    }

    this.ngZone.run(() => this.outsideClick.emit());
  }

  updateDropDownPostion() {
    const parentDomRect = this.parentElem.getBoundingClientRect();
    const selectDomRect = this.select.getBoundingClientRect();
    const offsetLeft = selectDomRect.left - parentDomRect.left;
    this.dropDown.style.left = offsetLeft + 'px';
    if (this.isSetWidth) {
      this.dropDown.style.width = selectDomRect.width + 'px';
      this.dropDown.style.minWidth = selectDomRect.width + 'px';
    }
    if (this.currentPosition === 'top' && !this.isTopPositionOnly) {
      this.dropDown.style.bottom =
        parentDomRect.bottom - selectDomRect.top + 7 + 'px';
      this.dropDown.style.top = 'auto';
    } else {
      this.dropDown.style.top =
        selectDomRect.bottom - parentDomRect.top + 7 + 'px';
      this.dropDown.style.bottom = 'auto';
    }
  }
  @HostListener('window:resize', ['$event'])
  onResize(event?: Event) {
    this.handleDropdownPosition();
  }
  private _calculateCurrentPosition(dropdownEl: HTMLElement) {
    const selectRect: ClientRect = this.select.getBoundingClientRect();
    const scrollTop =
      document.documentElement.scrollTop || document.body.scrollTop;
    const offsetTop = selectRect.top + window.pageYOffset;
    const height = selectRect.height;
    const dropdownHeight = dropdownEl.getBoundingClientRect().height;
    if (
      offsetTop + height + dropdownHeight >
      scrollTop + document.documentElement.clientHeight
    ) {
      return 'top';
    } else {
      return 'bottom';
    }
  }
  private handleDropdownPosition() {
    this.currentPosition = this._calculateCurrentPosition(this.dropDown);
    this.updateDropDownPostion();
  }
}
